import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const DEFAULT_INTRO = 'Share what is on your mind. Your confession will stay anonymous to the community.';
const DEFAULT_IMAGE_HELP = 'Optional: paste an image URL if there is a photo, screenshot, or artwork that belongs with your confession. You can leave this blank.';
const DEFAULT_BUTTON_LABEL = 'Submit Confession';

export default function SubmitConfessionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [settings, setSettings] = useState({
    submission_mode: 'google_form',
    google_form_url: '',
    confession_form_intro: '',
    confession_image_help_text: '',
    confession_submit_button_text: DEFAULT_BUTTON_LABEL,
  });
  const [formData, setFormData] = useState({
    confession: '',
    imageUrl: '',
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const nativeFormEnabled = settings.submission_mode === 'native_encrypted' || settings.submission_mode === 'both';
  const showGoogleFormFallback = settings.submission_mode === 'both' && !!settings.google_form_url;

  const introText = settings.confession_form_intro?.trim() || DEFAULT_INTRO;
  const imageHelpText = settings.confession_image_help_text?.trim() || DEFAULT_IMAGE_HELP;
  const submitLabel = settings.confession_submit_button_text?.trim() || DEFAULT_BUTTON_LABEL;

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/settings/public`);
        setSettings({
          submission_mode: response.data.submission_mode || 'google_form',
          google_form_url: response.data.google_form_url || '',
          confession_form_intro: response.data.confession_form_intro || '',
          confession_image_help_text: response.data.confession_image_help_text || '',
          confession_submit_button_text: response.data.confession_submit_button_text || DEFAULT_BUTTON_LABEL,
        });
      } catch (error) {
        toast.error('Failed to load confession form settings.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const confession = formData.confession.trim();
    if (!confession) {
      toast.error('Please write your confession before submitting.');
      return;
    }

    const submit = async () => {
      setSubmitting(true);
      try {
        await axios.post(`${API_URL}/api/confessions/submit`, {
          confession,
          imageLink: formData.imageUrl.trim() || undefined,
        });
        navigate('/submit-confession/success', { state: { fromSubmission: true } });
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to submit your confession.');
      } finally {
        setSubmitting(false);
      }
    };

    submit();
  };

  const handleGoogleFormFallback = async () => {
    try {
      await axios.post(`${API_URL}/api/confessions/google-form-redirect`);
    } catch (error) {
      // Intentionally silent: from the user's perspective this should just open the Google Form.
    } finally {
      window.location.href = settings.google_form_url;
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!nativeFormEnabled) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Card className="border-amber-500/40 bg-amber-50/70 dark:bg-amber-900/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-900 dark:text-amber-300">
              <AlertTriangle className="w-5 h-5" />
              Native submission is not live yet
            </CardTitle>
            <CardDescription className="text-amber-800/90 dark:text-amber-300/80">
              This site is still configured to use the external Google Form for confession submissions.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row">
            <Button className="btn-primary" onClick={() => navigate('/')}>
              Back to Home
            </Button>
            <Button variant="outline" asChild>
              <Link to="/about">View About</Link>
            </Button>
            {settings.google_form_url && (
              <Button variant="outline" asChild>
                <a href={settings.google_form_url} target="_blank" rel="noopener noreferrer">
                  Use Google Form Instead
                </a>
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user?.email_verified) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Card className="border-amber-500/40 bg-amber-50/70 dark:bg-amber-900/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-900 dark:text-amber-300">
              <Shield className="w-5 h-5" />
              Verify your account first
            </CardTitle>
            <CardDescription className="text-amber-800/90 dark:text-amber-300/80">
              Verified accounts are required before someone can use the native confession form.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="btn-primary" onClick={() => navigate('/verify-account')}>
              Verify Account
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="submit-confession-page">
      <Card className="bg-card/90 backdrop-blur-sm border-white/10 shadow-xl">
        <CardHeader className="space-y-4">
          <div>
            <CardTitle className="font-serif text-3xl">Submit a Confession</CardTitle>
            <CardDescription className="mt-3 text-base leading-7 text-foreground/80">
              {introText}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="confession">Confession</Label>
              <Textarea
                id="confession"
                name="confession"
                value={formData.confession}
                onChange={handleChange}
                placeholder="Write your confession here..."
                className="min-h-[240px]"
                maxLength={50000}
                data-testid="confession-text-input"
              />
            </div>

            <div className="space-y-3">
              <div className="rounded-xl border border-border/70 bg-muted/30 p-4">
                <p className="text-sm leading-6 text-muted-foreground">{imageHelpText}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="imageUrl">Image URL (optional)</Label>
                <Input
                  id="imageUrl"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                  maxLength={2048}
                  data-testid="confession-image-url-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="btn-primary w-full sm:w-auto"
              data-testid="submit-confession-form-btn"
              disabled={submitting}
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {submitLabel}
            </Button>
            {showGoogleFormFallback && (
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto"
                data-testid="google-form-fallback-btn"
                onClick={handleGoogleFormFallback}
              >
                Use Google Form Instead
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
